import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import FiltersSidebar from "@/components/FiltersSidebar";
import ListingCard from "@/components/ListingCard";
import AlertFeed from "@/components/AlertFeed";
import StatsBar from "@/components/StatsBar";
import { useListings, useCreateWatch, useDesktopInfo, useAccounts, usePayments } from "@/lib/queries";
import { api } from "@/lib/cr";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const DEFAULT_FILTERS = {
  q: "",
  game: "",
  set_name: "",
  product_type: "",
  rarity: "",
  retailer_id: "",
  region: "",
  price_min: "",
  price_max: "",
  deal_score_min: 0,
  in_stock: undefined,
  sort: "recent",
};

export default function RadarPage() {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const { data: listings = [], isFetching } = useListings(filters);
  const createWatch = useCreateWatch();
  const { data: desktopInfo } = useDesktopInfo();
  const { data: accounts = [] } = useAccounts();
  const { data: payments = [] } = usePayments();
  const desktopMode = !!desktopInfo?.desktop_mode;

  // Per-retailer 1-Click readiness: at least one linked store account + a default payment method
  const credByRetailer = useMemo(() => {
    const map = {};
    const defaultPayment = payments.find((p) => p.is_default) || payments[0];
    if (!defaultPayment) return map;
    for (const a of accounts) {
      if (!map[a.retailer_id]) map[a.retailer_id] = { account_id: a.id, payment_id: defaultPayment.id };
    }
    return map;
  }, [accounts, payments]);

  const handleQuickCart = async (listing) => {
    const cred = credByRetailer[listing.retailer_id];
    if (!cred) {
      toast.error("Link a store account first", {
        description: `Save your ${listing.retailer_name} login in /app/accounts to enable 1-Click cart`,
      });
      return;
    }
    try {
      const { data } = await api.post("/scalp/quick-cart", {
        listing_id: listing.id,
        store_account_id: cred.account_id,
        payment_method_id: cred.payment_id,
      });
      toast.success("Browser opening…", { description: data.message });
    } catch (e) {
      if (e?.response?.status === 503) {
        toast.error("1-Click cart needs the .exe", {
          description: "This feature only works when running CardRadar locally. Click 'Open cart' instead to land on the product page.",
        });
      } else {
        toast.error("Couldn't start 1-Click cart", { description: e?.response?.data?.detail || e.message });
      }
    }
  };

  const handleQuickWatch = (listing) => {
    createWatch.mutate(
      {
        name: `${listing.set_name} — ${listing.product_type}`,
        game: listing.game,
        set_name: listing.set_name,
        product_type: listing.product_type,
        retailers: [listing.retailer_id],
        regions: [listing.region],
        max_price: Math.ceil(listing.price * 1.05),
        in_stock_only: true,
        notify: true,
      },
      {
        onSuccess: () => toast.success("Added to watchlist", { description: listing.title }),
        onError: () => toast.error("Failed to add watchlist item"),
      }
    );
  };

  return (
    <div className="grid grid-cols-12 h-[calc(100vh-3.75rem)] overflow-hidden">
      <div className="col-span-3 xl:col-span-2 overflow-hidden">
        <FiltersSidebar
          filters={filters}
          onChange={setFilters}
          onReset={() => setFilters(DEFAULT_FILTERS)}
        />
      </div>

      <div className="col-span-6 xl:col-span-7 overflow-y-auto p-4 flex flex-col gap-4" data-testid="listings-pane">
        <StatsBar />
        {/* Live feed banner — cloud shows REAL listings only */}
        <div className="bg-[#22C55E]/5 border border-[#22C55E]/30 px-3 py-2.5 rounded-sm flex items-start gap-2.5" data-testid="live-feed-banner">
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#22C55E] font-mono mt-0.5 whitespace-nowrap flex items-center gap-1">
            <span className="inline-block w-1.5 h-1.5 bg-[#22C55E] rounded-full animate-pulse"></span>
            LIVE FEED
          </span>
          <div className="text-[11px] text-[#A3A3A3] leading-relaxed">
            Every listing on this page is real — pulled from the retailer&apos;s live feed. Cloud feed covers <strong className="text-white">TCGplayer + Pokemon Center</strong>.
            For all <strong className="text-white">15 retailers</strong> including JB Hi-Fi, Target AU, Kmart, BIG W, EB Games, and the auto-buy weapon → <Link to="/app/desktop" className="text-[#EAB308] underline font-bold">install the desktop app</Link>.
          </div>
        </div>
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-2xl tracking-tighter font-black uppercase">
            Live <span className="text-[#EAB308]">Inventory</span>
          </h1>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#737373] font-mono">
            {isFetching && <Loader2 className="w-3 h-3 animate-spin" />}
            {listings.length} results
          </div>
        </div>

        {listings.length === 0 ? (
          <div className="border border-dashed border-[#262626] bg-[#0a0a0a] p-10 text-center text-[#737373] rounded-sm">
            <div className="text-sm">No listings match your filters.</div>
            <div className="text-xs mt-1 text-[#525252]">Try resetting filters or starting the scanner.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="listings-grid">
            {listings.map((l) => (
              <ListingCard
                key={l.id}
                listing={l}
                onWatch={handleQuickWatch}
                onQuickCart={handleQuickCart}
                quickCartReady={desktopMode && !!credByRetailer[l.retailer_id]}
              />
            ))}
          </div>
        )}
      </div>

      <div className="col-span-3 overflow-hidden">
        <AlertFeed />
      </div>
    </div>
  );
}
